import React, { useEffect, useState } from "react";
import { Button, Header, Icon, Table, Pagination, Segment, Label, Grid, Input, Modal } from "semantic-ui-react";
import { toast } from "react-toastify";
import { ToasterConfig } from "../../../config/ToasterConfig";
import TooltipInfo from "../../tooltip/tooltip";
import { SessionUser } from "../../../models/session-user";
import SessionStorage from "../../../store/session";
import { useNavigate } from "react-router-dom";
import ReferenceForm from "./create-reference-form";
import useReferenceService from "../../../services/backoffice/hooks/useReferenceService";
import { ReferenceFilters } from "../../../models/reference-filters";
import { ReferenceDto } from "../../../models/reference-operating-hour-dto";
import moment from "moment";

interface ReferencesListProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
}

const ReferencesList: React.FC<ReferencesListProps> = ({ isModal = false, open = false, onClose }) => {
    const [references, setReferences] = useState<ReferenceDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<ReferenceFilters>({ Page: 1 });
    const [totalPages, setTotalPages] = useState<number>(0);
    const { getReferences, deleteReference, activateReference, deactivateReference } = useReferenceService();
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [editReferenceId, setEditReferenceId] = useState<string | null>(null);
    const [viewReferenceId, setViewReferenceId] = useState<string | null>(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user);
        }
        updateSessionUser();
    }, []);

    useEffect(() => {
        async function loadReferences() {
            setLoading(true);
            try {
                const response = await getReferences(filter);
                setReferences(response.itens);
                setTotalPages(response.totalPages);
            } catch (err: any) {
                setError("Erro ao carregar referências.");
            } finally {
                setLoading(false);
            }
        }
        loadReferences();
    }, [filter]);

    const handleDelete = async (id: string) => {
        try {
            const result = window.confirm("Tem certeza que deseja excluir esta referência?");
            if (result) {
                await deleteReference(id);
                toast.success("Referência excluída com sucesso", ToasterConfig.success);
                setReferences(references.filter((r) => r.id !== id));
            }
        } catch (err: any) {
            toast.error("Erro ao excluir referência", ToasterConfig.error);
        }
    };

    const handleActivation = async (reference: ReferenceDto) => {
        try {
            const result = window.confirm(`Tem certeza que deseja ${reference.isActive ? 'desativar' : 'ativar'} esta referência?`);
            if (result) {
                if (reference.isActive) {
                    await deactivateReference(reference.id);
                } else {
                    await activateReference(reference.id);
                }
                setFilter({ ...filter }); // Atualiza a lista
                toast.success(`Referência ${reference.isActive ? 'desativada' : 'ativada'} com sucesso.`, ToasterConfig.success);
            }
        } catch (err: any) {
            toast.error("Erro ao alterar status da referência", ToasterConfig.error);
        }
    };

    const handleEdit = (id: string) => {
        setEditReferenceId(id);
        setModalOpen(true);
    };

    const handleNew = () => {
        setEditReferenceId(null);
        setModalOpen(true);
    };

    const handleView = (id: string) => {
        setViewReferenceId(id);
        setViewModalOpen(true);
    };

    // Função para verificar se pode fechar o modal de edição
    const canCloseEditModal = (): boolean => {
        if (hasUnsavedChanges) {
            return window.confirm('Tem certeza que deseja sair? Todas as alterações não salvas serão perdidas.');
        }
        return true;
    };

    const formContent = (
        <Segment fluid style={{ maxWidth: "90%", margin: "5px auto", padding: "20px" }}>
            <Header as="h3" textAlign="center" color={'green'}>
                Referências
            </Header>
            <Table.Row>
                <Table.HeaderCell>
                    {sessionUser?.userData?.profile === 'Admin' && (
                        <Button color="green" onClick={handleNew}>
                            <Icon name='plus' /> Novo
                        </Button>
                    )}
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color="green">
                        <Icon name={showFilters ? 'hide' : 'unhide'} />
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>
            </Table.Row>

            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={6}>
                                <label htmlFor="code">Código</label>
                                <Input
                                    fluid
                                    id="code"
                                    icon="search"
                                    type="text"
                                    placeholder="Código da referência"
                                    value={filter?.referenceCode ?? ''}
                                    onChange={(event) => setFilter({ ...filter, referenceCode: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={6}>
                                <label htmlFor="name">Nome</label>
                                <Input
                                    fluid
                                    id="name"
                                    icon="search"
                                    type="text"
                                    placeholder="Nome da referência"
                                    value={filter?.name ?? ''}
                                    onChange={(event) => setFilter({ ...filter, name: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={2} verticalAlign="bottom">
                                <Button color="green" onClick={() => setFilter({ ...filter, Page: 1 })}>Buscar</Button>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            )}

            <Table color="green">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Código</Table.HeaderCell>
                        <Table.HeaderCell>Nome</Table.HeaderCell>
                        <Table.HeaderCell>Descrição</Table.HeaderCell>
                        <Table.HeaderCell>Visível</Table.HeaderCell>
                        <Table.HeaderCell>Carga</Table.HeaderCell>
                        <Table.HeaderCell>Descarga</Table.HeaderCell>
                        <Table.HeaderCell>Criado em</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {references?.map((reference) => (
                        <Table.Row key={reference.id}>
                            <Table.Cell>
                                <Button.Group>
                                    <TooltipInfo
                                        content="Visualizar referência"
                                        trigger={
                                            <Button
                                                color="blue"
                                                onClick={() => handleView(reference.id)}
                                                icon="eye"
                                            />
                                        }
                                    />
                                    
                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Editar referência"
                                            trigger={
                                                <Button
                                                    color="green"
                                                    onClick={() => handleEdit(reference.id)}
                                                    icon="edit"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Excluir referência"
                                            trigger={
                                                <Button
                                                    color="red"
                                                    onClick={() => handleDelete(reference.id)}
                                                    icon="trash"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content={reference.isActive ? "Desativar referência" : "Ativar referência"}
                                            trigger={
                                                <Button
                                                    color={reference.isActive ? 'red' : 'green'}
                                                    onClick={() => handleActivation(reference)}
                                                    icon={reference.isActive ? 'power off' : 'power'}
                                                />
                                            }
                                        />
                                    )}
                                </Button.Group>
                            </Table.Cell>
                            <Table.Cell>{reference.referenceCode}</Table.Cell>
                            <Table.Cell>{reference.name}</Table.Cell>
                            <Table.Cell>{reference.description}</Table.Cell>
                            <Table.Cell>
                                <Label color={reference.isReferenceVisible ? 'green' : 'red'}>
                                    {reference.isReferenceVisible ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                            <Table.Cell>
                                <Label color={reference.isLoad ? 'green' : 'red'}>
                                    {reference.isLoad ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                            <Table.Cell>
                                <Label color={reference.isUnload ? 'green' : 'red'}>
                                    {reference.isUnload ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                            <Table.Cell>{moment(reference.createdAt).format('DD/MM/YY HH:mm')}</Table.Cell>
                            <Table.Cell>
                                <Label color={reference.isActive ? 'green' : 'red'}>
                                    {reference.isActive ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>

            <Pagination
                activePage={filter?.Page || 1}
                onPageChange={(e, { activePage }) => setFilter({ ...filter, Page: activePage as number })}
                totalPages={totalPages || 1}
                siblingRange={1}
                boundaryRange={1}
                firstItem={null}
                lastItem={null}
                pointing
                secondary
            />
        </Segment>
    );

    return (
        <>
            <Modal
                open={viewModalOpen}
                onClose={() => {
                    setViewModalOpen(false);
                    setViewReferenceId(null);
                }}
                closeOnDimmerClick={true}
                closeOnEscape={true}
                size="large"
                style={{ width: '90%', maxWidth: '900px' }}
            >
                <Modal.Content>
                    {viewReferenceId && (
                        <ReferenceForm 
                            referenceId={viewReferenceId} 
                            isModal={true} 
                            onClose={() => {
                                setViewModalOpen(false);
                                setViewReferenceId(null);
                            }}
                            readOnly={true}
                        />
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button basic onClick={() => {
                        setViewModalOpen(false);
                        setViewReferenceId(null);
                    }}>
                        Fechar
                    </Button>
                </Modal.Actions>
            </Modal>

            <Modal
                open={modalOpen}
                onClose={() => {
                    if (canCloseEditModal()) {
                        setModalOpen(false);
                        setEditReferenceId(null);
                        setHasUnsavedChanges(false);
                    }
                }}
                closeOnDimmerClick={!hasUnsavedChanges}
                closeOnEscape={!hasUnsavedChanges}
                size="large"
                style={{ width: '90%', maxWidth: '900px' }}
            >
                <Modal.Content>
                    <ReferenceForm
                        referenceId={editReferenceId ?? undefined}
                        isModal={true}
                        onClose={(forceClose = false) => {
                            if (hasUnsavedChanges && !forceClose) {
                                if (window.confirm('Tem certeza que deseja sair? Todas as alterações não salvas serão perdidas.')) {
                                    setModalOpen(false);
                                    setEditReferenceId(null);
                                    setHasUnsavedChanges(false);
                                }
                            } else {
                                setModalOpen(false);
                                setEditReferenceId(null);
                                setHasUnsavedChanges(false);
                            }
                        }}
                        onFormModified={setHasUnsavedChanges}
                        readOnly={false}
                    />
                </Modal.Content>
            </Modal>

            {isModal ? (
                <Modal
                    open={open}
                    onClose={onClose}
                    size="large"
                >
                    <Modal.Header>Referências</Modal.Header>
                    <Modal.Content>
                        {formContent}
                    </Modal.Content>
                </Modal>
            ) : (
                formContent
            )}
        </>
    );
};

export default ReferencesList;
